/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1679961600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Mar 28 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "poplar",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Poplar",
          "depth": 0,
          "id": "connect-to-poplar"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Segment",
          "depth": 1,
          "id": "segment"
        }, {
          "value": "Campaign trigger",
          "depth": 1,
          "id": "campaign-trigger"
        }, {
          "value": "Suppression lists",
          "depth": 1,
          "id": "suppression-lists"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      a: "a",
      img: "img",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      li: "li",
      ol: "ol"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Segment"
              })
            }), _jsx(_components.td, {
              children: "Add members to existing Poplar segments"
            }), _jsx(_components.td, {
              children: "Add"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Campaign Trigger"
              })
            }), _jsx(_components.td, {
              children: "Trigger mail campaigns with context from your records"
            }), _jsx(_components.td, {
              children: "Add"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Suppression Lists"
              })
            }), _jsx(_components.td, {
              children: "Add members to Poplar's Do Not Mail list"
            }), _jsx(_components.td, {
              children: "Add"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-poplar",
        children: "Connect to Poplar"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), "\nbutton. Select ", _jsx(_components.strong, {
          children: "Poplar"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Poplar"
        }), " by entering a Poplar ", _jsx(_components.strong, {
          children: "API key"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To access your API keys, navigate to ", _jsx(_components.strong, {
          children: "Integrations"
        }), " > ", _jsx(_components.strong, {
          children: "API"
        }), " in Poplar. If you want to create a new one, click ", _jsx(_components.strong, {
          children: "Generate Access Token"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-poplar-api-key.png",
          alt: "Finding an API key in the Poplar dashboard"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["We recommend using a ", _jsx(_components.strong, {
          children: "Production"
        }), " mode API key. If you use a ", _jsx(_components.strong, {
          children: "Test"
        }), " mode API key, you can only use the Campaign Trigger sync type due to Poplar API limitations. To learn more about Poplar API keys, check out ", _jsx(_components.a, {
          href: "https://docs.heypoplar.com/article/246-test-production-tokens",
          children: "Poplar's docs"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Poplar destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull\ndata from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview\npage"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and\nthe Poplar destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "segment",
        children: "Segment"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports adding customer records based on rows from your model to any of your Poplar audiences."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows from your model to customer records in Poplar on the fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Identifier"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Hashed Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Address"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "campaign-trigger",
        children: "Campaign trigger"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you use records from your models to trigger mail campaigns. This sync triggers a campaign for your Poplar customers based on the recipient's information."
      }), "\n", _jsx(_components.p, {
        children: "Once configured, your Hightouch sync triggers your campaign once rows appear in your model. Hightouch triggers your campaign for every row in your model the first time you run the sync, whenever you do a full resync, and for every new row added to your query results in subsequent syncs."
      }), "\n", _jsx(_components.h4, {
        id: "creative-selection",
        children: "Creative selection"
      }), "\n", _jsxs(_components.p, {
        children: ["You can optionally provide a Poplar ", _jsx(_components.strong, {
          children: "creative ID"
        }), " for the campaign you want to trigger. If you don't enter a creative ID, Poplar uses the default creative. If you haven't sent a default creative, Poplar alternates between all active creatives."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to the following native columns in a Poplar campaign trigger:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Company"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "First Name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Last Name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Address 1"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Address 2"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "City"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "State"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Postal Code"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Identifier"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Address"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also map model columns to use in ", _jsx(_components.a, {
          href: "https://docs.heypoplar.com/article/203-merge-tags",
          children: "Poplar merge tags"
        }), "."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["This destination doesn't check for duplicates. Hightouch recommends that you set ", _jsx(_components.a, {
            href: "https://docs.heypoplar.com/article/204-suppression-settings",
            children: "suppression control"
          }), " in Poplar to prevent duplicate campaign messages in case your model has duplicates."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "testing",
        children: "Testing"
      }), "\n", _jsx(_components.p, {
        children: "You can test your campaign trigger sync by following these steps:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Use a ", _jsx(_components.strong, {
            children: "Test"
          }), " mode API key within your destination configuration."]
        }), "\n", _jsx(_components.li, {
          children: "Set up your campaign trigger sync and run it on Hightouch."
        }), "\n", _jsxs(_components.li, {
          children: ["Download mailing data by navigating to ", _jsx(_components.strong, {
            children: "Integrations"
          }), " > ", _jsx(_components.strong, {
            children: "Mailing Data"
          }), " in Poplar."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-poplar-mailing-data.png",
          alt: "Download Poplar mailing data"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsx(_components.li, {
          children: "Verify that the trigger was set off by comparing the synced records with mailing data."
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["If you need to make corrections and thus want to re-run the sync, click ", _jsx(_components.strong, {
            children: "Resync full query"
          }), " next to the ", _jsx(_components.strong, {
            children: "Run"
          }), " button."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "suppression-lists",
        children: "Suppression lists"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports syncing rows from your model to customer records in your Poplar ", _jsx(_components.strong, {
          children: "Do Not Mail"
        }), " list. Members of this list are automatically suppressed across all campaigns."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You can add customer records to the Poplar' ", _jsx(_components.strong, {
          children: "Do Not Mail"
        }), " using these fields:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Identifier"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Hashed Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Address"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can select one or more of the identifiers for a single record."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
